'use client';

import {
  faCheck,
  faLockKeyhole,
  faMessage,
  faSquare,
  faXmark,
} from '@fortawesome/pro-regular-svg-icons';
import { faMessageLines } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Checkbox, Tooltip } from '@pickleballinc/react-ui';
import { useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { OptInPlayerfromPlayingListRes } from '@/app/api/v1/leagues/rsvps/saveOptInOutWaitPlayer/route';
import ConfirmationModal from '@/modules/league/new-components/header/ConfirmationModal';
import {
  useSaveCheckin,
  useSaveOptIn,
  useSaveOptOutMultiplePlayers,
  useWaitlist,
} from '@/modules/league/new-components/rsvp-components/hooks';
import SendMessageModal from '@/modules/league/new-components/rsvp-components/SendMessageModal';
import { GameDayInfo, Player } from '@/modules/league/new-components/types';
import { useLoader } from '@/modules/league/PickleballLoaderContext';
import { getConnectDUPRURL } from '@/utils/helpers/getConnectDUPRURL';
import { getNumberSuffix } from '@/utils/helpers/getNumberSuffix';

interface PlayerItemProps {
  player: Player;
  headerContent: GameDayInfo;
  userUuid: string;
  cardName: string;
  isAuthorized: boolean | null;
  playersForOptIn: Player[];
  playersForOptOut: Player[];
  playersForRemove: Player[];
  playersForWait: Player[];
  playersForCheckIn: number;
  setPlayersForCheckIn: React.Dispatch<React.SetStateAction<number>>;
  numberOfPlayers: number;
  toggleOptIn: (player: Player) => Promise<void>;
  toggleOptOut: (player: Player) => Promise<void>;
  toggleRemove: (player: Player) => Promise<void>;
  toggleWait: (player: Player) => Promise<void>;
  canMoveToWait: boolean;
  canOptIn: boolean;
  isOptInButtonDisabled: boolean;
  duprPromptEnabled: boolean;
  setUncheckedPlayers: React.Dispatch<React.SetStateAction<Player[]>>;
  uncheckedPlayers: Player[];
  chargeOptIn: boolean;
  chargeOptInURL: string;
  slug: string;
}

export const PlayerItem = ({
  player,
  headerContent,
  cardName,
  index,
  isAuthorized,
  userUuid,
  playersForOptIn,
  playersForOptOut,
  playersForRemove,
  playersForWait,
  toggleOptIn,
  toggleOptOut,
  toggleRemove,
  toggleWait,
  playersForCheckIn,
  setPlayersForCheckIn,
  numberOfPlayers,
  canMoveToWait,
  canOptIn,
  isOptInButtonDisabled,
  duprPromptEnabled,
  setUncheckedPlayers,
  uncheckedPlayers,
  chargeOptIn,
  chargeOptInURL,
  slug,
}: PlayerItemProps & { index: number }) => {
  const queryClient = useQueryClient();
  const router = useRouter();
  const [selectedPlayer, setSelectedPlayer] = useState({
    fullName: '',
    userId: '',
  });
  const [selectedPlayerPartner, setSelectedPlayerPartner] = useState({
    fullName: '',
    userId: '',
  });
  const [optOutSelection, setOptOutSelection] = useState<Player>();

  const [showMessage, setShowMessage] = useState(false);
  const [show, setShow] = useState(false);
  const [showWait, setShowWait] = useState(false);
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [showDoupleConfirmOptOutSinglePlayer, setShowDoupleConfirmOptOutSinglePlayer] =
    useState(false);
  const [isUserChecked, setIsUserChecked] = useState(player.UserCheckIn_1);
  const [location, setLocation] = useState('');

  useEffect(() => {
    setIsUserChecked(player.UserCheckIn_1);
  }, [player]);

  useEffect(() => {
    if (playersForCheckIn === numberOfPlayers) setIsUserChecked(true);
  }, [playersForCheckIn]);

  useEffect(() => {
    setLocation(window.location.href);
  }, []);

  const { mutate: optOutMultiplePlayers } = useSaveOptOutMultiplePlayers();
  const { mutate: optInPlayer } = useSaveOptIn();
  const { mutate: saveCheckin } = useSaveCheckin();
  const { mutate: addOrRemoveFromWaitlist } = useWaitlist();

  const { setLoading } = useLoader();
  const isUserAlsoPlayer = userUuid?.toLowerCase() === player?.UserID_1?.toLowerCase();

  // Determine if the player's membership is active for opt-in (only NOT Playing card)
  // If the league allows non-members to sign up, membership is always considered active, so opt-in is shown
  // If the league requires only members for opt-in, check ClubMemberStatus_1
  // If ClubMemberStatus_1 !== 1, show expired member, hide opt-in option
  const isPlayerMembershipActive =
    headerContent.details.AllowNonMemberSignUp ||
    (!headerContent.details.AllowNonMemberSignUp && player.ClubMemberStatus_1 === 1);
  const playerNotInRank = player.CanPlayInRank_1 === 0;
  const playerNotInAge = player.CanPlayInAge_1 === 0;

  const showRestrictionMessage =
    !headerContent.details.isLocked &&
    cardName === 'Not Playing' &&
    (!isPlayerMembershipActive || playerNotInAge || playerNotInRank);

  const gameDayTitle = moment.parseZone(headerContent.details.FromDateTime).format('DDD MMM DD');

  const canShowPlayingDate = headerContent?.details?.showPlayingDate;
  const canShowWaitlistDate = headerContent?.details?.showWaitlistDate;

  return (
    <>
      <div
        className={`flex h-auto w-full flex-col border px-2 py-1.5 md:px-4 ${userUuid || isAuthorized ? 'gap-3 py-2' : ''}  ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'} ${isUserAlsoPlayer ? 'border-leagues-500/90' : 'border-gray-50'}`}
      >
        <div className={`flex flex-col ${isAuthorized ? '' : ''}`}>
          <div className='flex flex-row gap-1 md:gap-2'>
            {cardName === 'Playing' && (
              <div className={`flex justify-center pt-[2px]`}>
                {!isUserChecked && !isAuthorized ? (
                  <FontAwesomeIcon
                    icon={faXmark}
                    size='lg'
                    className='mr-1 cursor-not-allowed text-error-500'
                    key={player.UserID_1}
                  />
                ) : uncheckedPlayers.includes(player) && isAuthorized ? (
                  <FontAwesomeIcon
                    icon={faSquare}
                    size='lg'
                    onClick={() => {
                      if ((isAuthorized || userUuid) && !headerContent.details.isMatchStarted) {
                        setIsUserChecked(!isUserChecked);
                        setPlayersForCheckIn(playersForCheckIn + 1);
                        setUncheckedPlayers((prevPlayers) => {
                          if (prevPlayers.some((p) => p.UserID_1 === player.UserID_1)) {
                            return prevPlayers.filter((p) => p.UserID_1 !== player.UserID_1);
                          } else {
                            return [...prevPlayers, player];
                          }
                        });
                        saveCheckin(
                          { matchDateId: headerContent.details.MatchDateID, player: player },
                          {
                            onSuccess: (data) => {
                              toast.success(data.message);
                              queryClient.invalidateQueries(['league_rsvp']);
                            },
                            onError: () => {
                              toast.error('Something went wrong. Try again.');
                              setLoading(false);
                            },
                          }
                        );
                      }
                    }}
                    className={`text-leagues-500/90 ${
                      (isAuthorized ||
                        userUuid === player.UserID_1 ||
                        userUuid === player.UserID_2) &&
                      !headerContent.details.isMatchStarted
                        ? 'cursor-pointer hover:text-leagues-500'
                        : ' cursor-not-allowed'
                    }`}
                    key={player.UserID_1}
                  />
                ) : (
                  <FontAwesomeIcon
                    icon={faCheck}
                    className={`pt-[2px] text-leagues-500/90 ${
                      (isAuthorized ||
                        userUuid === player.UserID_1 ||
                        userUuid === player.UserID_2) &&
                      !headerContent.details.isMatchStarted
                        ? 'cursor-pointer hover:text-leagues-500'
                        : ' cursor-not-allowed'
                    }`}
                    size='lg'
                    onClick={() => {
                      if (
                        (isAuthorized ||
                          userUuid === player.UserID_1 ||
                          userUuid === player.UserID_2) &&
                        !headerContent.details.isMatchStarted
                      ) {
                        setIsUserChecked(!isUserChecked);
                        setPlayersForCheckIn(playersForCheckIn - 1);
                        setUncheckedPlayers((prevPlayers) => {
                          if (prevPlayers.some((p) => p.UserID_1 === player.UserID_1)) {
                            return prevPlayers.filter((p) => p.UserID_1 !== player.UserID_1);
                          } else {
                            return [...prevPlayers, player];
                          }
                        });
                        saveCheckin(
                          { matchDateId: headerContent.details.MatchDateID, player: player },
                          {
                            onSuccess: (data) => {
                              toast.success(data.message);
                              queryClient.invalidateQueries(['league_rsvp']);
                            },
                            onError: () => {
                              toast.error('Something went wrong. Try again.');
                              setLoading(false);
                            },
                          }
                        );
                      }
                    }}
                  />
                )}
              </div>
            )}
            <div
              className={`flex h-5 w-10 justify-center ${isAuthorized && (player.FirstName_2 === undefined || player.FirstName_2 === '') ? '' : 'items-center pt-1'}`}
            >
              <p className='font-inter pt-[3px] text-left text-sm font-normal'>
                ({player.StatsSeedOnGameDay}
                {getNumberSuffix(parseInt(player.StatsSeedOnGameDay))})
              </p>
            </div>
            <div className='flex w-full justify-between'>
              <div className='flex w-auto flex-col gap-1'>
                <p className='font-inter line-clamp-3 text-left text-base font-semibold'>
                  {player.PlayerFullName_1}
                </p>
                <div className='flex flex-col gap-1'>
                  {duprPromptEnabled && !player.DUPR1Active ? (
                    <div className={`flex flex-row`}>
                      <span className='text-xs font-medium text-gray-600'>
                        {userUuid && player?.UserID_1?.toLowerCase() === userUuid.toLowerCase() ? (
                          <a
                            className='text-primary-500 underline'
                            href={getConnectDUPRURL(
                              player.UserID_1,
                              'l',
                              window.location.href,
                              window.location.href
                            )}
                            aria-label='Link to connect to DUPR'
                          >
                            Connect To DUPR
                          </a>
                        ) : (
                          '(Not Linked to DUPR)'
                        )}
                      </span>
                    </div>
                  ) : null}
                  {isAuthorized || isUserAlsoPlayer ? (
                    <div className='flex flex-row'>
                      <div
                        className={`flex w-full justify-between ${cardName === 'Waitlist' && headerContent.details.isLocked ? 'flex-col gap-1' : ''}`}
                      >
                        <div className='flex'>
                          <p className='text-sm font-normal'>{player.Phone_1}</p>
                          {headerContent.details?.DateLockScores === null &&
                          headerContent.details.showMessageToEveryone ? (
                            <Tooltip placement='top-end'>
                              <Tooltip.Trigger asChild>
                                <FontAwesomeIcon
                                  onClick={() => {
                                    if (
                                      player.SendInGameTexts_1 &&
                                      isAuthorized &&
                                      player.PlayerFullName_1 &&
                                      player.UserID_1
                                    ) {
                                      setShowMessage(true);
                                      setSelectedPlayer({
                                        fullName: player.PlayerFullName_1,
                                        userId: player.UserID_1,
                                      });
                                    }
                                  }}
                                  icon={player.SendInGameTexts_1 ? faMessageLines : faMessage}
                                  className={`ml-2 pt-[2px] text-leagues-300 ${player.SendInGameTexts_1 && isAuthorized ? 'cursor-pointer hover:!text-leagues-300/90' : ''} `}
                                  size='1x'
                                />
                              </Tooltip.Trigger>
                              <Tooltip.Content className='w-42 bg-opacity/75 rounded bg-black p-2 text-sm text-white'>
                                {player.SendInGameTexts_1
                                  ? 'Texting is turned on'
                                  : 'Texting is not turned on'}
                              </Tooltip.Content>
                            </Tooltip>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
              <div
                className={`flex flex-col md:gap-[10px] ${headerContent.details.isLocked ? 'gap-[10px]' : 'gap-[6px]'}`}
              >
                {!headerContent.details.isLocked &&
                !isAuthorized &&
                headerContent.details.isOptInActive &&
                (userUuid?.toLowerCase() === player.UserID_1?.toLowerCase() ||
                  userUuid?.toLowerCase() === player?.UserID_2?.toLowerCase()) &&
                (cardName === 'Playing' ||
                  (cardName === 'Not Playing' &&
                    isPlayerMembershipActive &&
                    (!isOptInButtonDisabled || (isOptInButtonDisabled && canMoveToWait)) &&
                    !playerNotInAge &&
                    !playerNotInRank)) ? (
                  <div className='flex items-center justify-end'>
                    <Button
                      key={player.UserID_1}
                      variant='primary'
                      className='max-h-8 rounded-md bg-leagues-700 !px-2 !py-1 !text-xs font-semibold text-white hover:bg-leagues-500'
                      onClick={() => {
                        setShow(true);
                        if (
                          player.PlayerFullName_2 === '' &&
                          player.PlayerFullName_1 &&
                          player.UserID_1
                        ) {
                          setSelectedPlayer({
                            fullName: player.PlayerFullName_1,
                            userId: player.UserID_1,
                          });
                        } else if (
                          player.PlayerFullName_2 &&
                          player.PlayerFullName_1 &&
                          player.UserID_1 &&
                          player.PlayerFullName_2 &&
                          player.UserID_2
                        ) {
                          setSelectedPlayer({
                            fullName: player.PlayerFullName_1,
                            userId: player.UserID_1,
                          });
                          setSelectedPlayerPartner({
                            fullName: player.PlayerFullName_2,
                            userId: player.UserID_2,
                          });
                        }
                        setOptOutSelection(player);
                      }}
                    >
                      {cardName === 'Playing'
                        ? 'Opt out'
                        : !isOptInButtonDisabled
                          ? 'Opt in'
                          : canMoveToWait
                            ? 'Wait'
                            : ''}
                    </Button>
                  </div>
                ) : showRestrictionMessage && !isAuthorized ? (
                  <div className='text-sm font-normal w-fit gap-2 flex flex-col'>
                    {!isPlayerMembershipActive ? (
                      <>
                        <p>Expired</p>
                        <p>Member</p>
                      </>
                    ) : playerNotInRank ? (
                      <>
                        <p>Wrong</p>
                        <p>Rank</p>
                      </>
                    ) : playerNotInAge ? (
                      <>
                        <p>Wrong</p>
                        <p>Age</p>
                      </>
                    ) : null}
                  </div>
                ) : null}

                {!headerContent.details.isLocked &&
                cardName === 'Waitlist' &&
                headerContent.details.isOptInActive &&
                !isAuthorized &&
                (userUuid === player.UserID_1 || userUuid === player.UserID_2) ? (
                  <p
                    className='flex cursor-pointer items-center justify-center text-xs font-semibold text-leagues-700 hover:text-leagues-500'
                    onClick={() => {
                      setShowWaitlist(true);
                      if (
                        player.PlayerFullName_1 &&
                        player.UserID_1 &&
                        player.PlayerFullName_2 === ''
                      ) {
                        setSelectedPlayer({
                          fullName: player.PlayerFullName_1,
                          userId: player.UserID_1,
                        });
                      } else if (
                        player.PlayerFullName_2 &&
                        player.PlayerFullName_1 &&
                        player.UserID_1 &&
                        player.PlayerFullName_2 &&
                        player.UserID_2
                      ) {
                        setSelectedPlayer({
                          fullName: player.PlayerFullName_1,
                          userId: player.UserID_1,
                        });
                        setSelectedPlayerPartner({
                          fullName: player.PlayerFullName_2,
                          userId: player.UserID_2,
                        });
                      }

                      setOptOutSelection(player);
                    }}
                  >
                    Remove
                  </p>
                ) : null}
                {(isAuthorized &&
                  headerContent.details.NumberOfFutureMatchDatesStarted === 0 &&
                  !headerContent.details.isMatchStarted &&
                  !headerContent.details.isLocked &&
                  player.UserID_1 !== null &&
                  cardName !== 'Not Playing') ||
                (((cardName === 'Not Playing' &&
                  !isOptInButtonDisabled &&
                  !canMoveToWait &&
                  !showRestrictionMessage) ||
                  (cardName === 'Not Playing' &&
                    isOptInButtonDisabled &&
                    canMoveToWait &&
                    !showRestrictionMessage) ||
                  (cardName === 'Not Playing' &&
                    !isOptInButtonDisabled &&
                    canMoveToWait &&
                    !showRestrictionMessage)) &&
                  isAuthorized &&
                  headerContent.details.NumberOfFutureMatchDatesStarted === 0 &&
                  !headerContent.details.isMatchStarted &&
                  !headerContent.details.isLocked &&
                  player.UserID_1 !== null) ? (
                  <label className='inline-flex min-w-[90px] cursor-pointer justify-end text-sm font-semibold uppercase not-italic leading-6 tracking-wide text-leagues-700 hover:!text-leagues-500/90 md:text-sm'>
                    <Checkbox
                      size='sm'
                      onChange={() => {
                        if (cardName === 'Not Playing' && !isOptInButtonDisabled && canOptIn) {
                          toggleOptIn(player);
                        } else {
                          if (
                            cardName === 'Not Playing' &&
                            isOptInButtonDisabled &&
                            canMoveToWait
                          ) {
                            toggleWait(player);
                          } else {
                            if (cardName === 'Playing') {
                              toggleOptOut(player);
                            } else {
                              toggleRemove(player);
                            }
                          }
                        }
                      }}
                      Text={() => (
                        <>
                          {cardName === 'Playing'
                            ? 'Opt OUT'
                            : cardName === 'Not Playing' && !isOptInButtonDisabled
                              ? 'Opt IN'
                              : cardName === 'Not Playing' && isOptInButtonDisabled && canMoveToWait
                                ? 'Wait'
                                : canMoveToWait
                                  ? 'Remove'
                                  : ''}
                        </>
                      )}
                      checked={
                        (cardName === 'Not Playing' &&
                          !isOptInButtonDisabled &&
                          playersForOptIn.some(
                            (arrPlayer) => arrPlayer.UserID_1 === player.UserID_1
                          )) ||
                        (cardName === 'Not Playing' &&
                          isOptInButtonDisabled &&
                          playersForWait.some(
                            (arrPlayer) => arrPlayer.UserID_1 === player.UserID_1
                          )) ||
                        (cardName === 'Playing' &&
                          playersForOptOut.some(
                            (arrPlayer) => arrPlayer.UserID_1 === player.UserID_1
                          )) ||
                        (cardName === 'Waitlist' &&
                          playersForRemove.some(
                            (arrPlayer) => arrPlayer.UserID_1 === player.UserID_1
                          ))
                      }
                      disabled={
                        cardName === 'Not Playing' && isOptInButtonDisabled
                          ? !canMoveToWait
                          : undefined
                      }
                      key={player.UserID_1}
                    />
                  </label>
                ) : showRestrictionMessage && isAuthorized ? (
                  <div className='text-sm font-normal w-fit gap-2 flex flex-col'>
                    {!isPlayerMembershipActive ? (
                      <>
                        <p>Expired</p>
                        <p>Member</p>
                      </>
                    ) : playerNotInRank ? (
                      <>
                        <p>Wrong</p>
                        <p>Rank</p>
                      </>
                    ) : playerNotInAge ? (
                      <>
                        <p>Wrong</p>
                        <p>Age</p>
                      </>
                    ) : null}
                  </div>
                ) : null}

                {headerContent.details.isLocked ? (
                  <div
                    className={`flex w-full justify-end ${isAuthorized && (player.FirstName_2 === undefined || player.FirstName_2 === '') ? 'pt-1' : 'items-center'}`}
                  >
                    <FontAwesomeIcon icon={faLockKeyhole} className=' text-gray-900' size='1x' />
                  </div>
                ) : null}
                {cardName === 'Playing' && (isAuthorized || canShowPlayingDate) ? (
                  <div className='flex w-auto'>
                    <p className='line-clamp-1 text-right text-xs font-normal'>
                      {player.DateOptIn}
                    </p>
                  </div>
                ) : null}
                {cardName === 'Waitlist' && (isAuthorized || canShowWaitlistDate) ? (
                  <div className='flex w-auto justify-end'>
                    <p className='line-clamp-1 text-right text-xs font-normal'>
                      {player.DateCreated}
                    </p>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
      <ConfirmationModal
        open={showWait}
        onClose={() => setShowWait(false)}
        onConfirm={() => {
          setLoading(true);
          if (optOutSelection) {
            addOrRemoveFromWaitlist(
              {
                matchDateId: headerContent.details.MatchDateID,
                player: optOutSelection,
                doing: 'in',
              },
              {
                onSuccess: (data) => {
                  if (data.message) {
                    toast.success(data.message);
                  }
                  queryClient.invalidateQueries(['league_rsvp']);
                  setLoading(false);
                },
                onError: () => {
                  toast.error('Something went wrong. Try again.');
                  setLoading(false);
                },
              }
            );
          }
          setShowWait(false);
        }}
        title='Are you sure?'
        message={`This will move ${selectedPlayer.fullName} ${
          selectedPlayerPartner && selectedPlayerPartner.fullName !== ''
            ? `and ${selectedPlayerPartner.fullName}`
            : ''
        } to the waitlist`}
        confirmButtonText='Move'
      />
      <ConfirmationModal
        open={show}
        onClose={() => setShow(false)}
        onConfirm={() => {
          if (cardName === 'Playing') {
            setShowDoupleConfirmOptOutSinglePlayer(true);
          } else if (optOutSelection && !isOptInButtonDisabled) {
            if (chargeOptIn) {
              router.push(`${process.env.NEXT_PUBLIC_PBRACKETS_URL}/${chargeOptInURL}`);
            } else {
              setLoading(true);
              optInPlayer(
                { matchDateId: headerContent.details.MatchDateID, player: optOutSelection },
                {
                  onSuccess: (data: OptInPlayerfromPlayingListRes) => {
                    if (data.message === 'No available spots') {
                      toast.error(data.message);
                      setLoading(false);
                    } else if (data.redirectPlayerToPay) {
                      router.push(
                        `${process.env.NEXT_PUBLIC_PBRACKETS_URL}/redirect_r.aspx?mf=l&mid=${headerContent.details.leagueId}&lahid=${data.responseData?.AttendeeHeaderID}&sid=${headerContent.details.sessionId}&mdid=${headerContent.details.MatchDateID}&cid=${headerContent.details.clubId}&todo=r&rulr=${encodeURIComponent(location)}`
                      );
                    } else {
                      if (data.message === 'Opted in successfully') {
                        toast.success(data.message);
                      } else {
                        toast.error(data.message);
                        setLoading(false);
                      }
                    }
                    queryClient.invalidateQueries(['league_rsvp']);
                  },
                  onError: () => {
                    toast.error('Something went wrong. Try again.');
                    setLoading(false);
                  },
                }
              );
            }
          } else if (optOutSelection && isOptInButtonDisabled && canMoveToWait) {
            setLoading(true);
            addOrRemoveFromWaitlist(
              {
                matchDateId: headerContent.details.MatchDateID,
                player: optOutSelection,
                doing: 'in',
              },
              {
                onSuccess: (data) => {
                  if (data.message) {
                    toast.success(data.message);
                  }
                  queryClient.invalidateQueries(['league_rsvp']);
                },
                onError: () => {
                  toast.error('Something went wrong. Try again.');
                  setLoading(false);
                },
              }
            );
          }

          setShow(false);
        }}
        title='Are you sure?'
        message={`This will ${cardName === 'Playing' ? 'opt out' : !isOptInButtonDisabled ? 'opt in' : canMoveToWait ? 'move' : ''} ${selectedPlayer.fullName} ${
          selectedPlayerPartner && selectedPlayerPartner.fullName !== ''
            ? `and ${selectedPlayerPartner.fullName}`
            : ''
        } ${cardName === 'Not Playing' && isOptInButtonDisabled ? 'to the waitlist' : ''}`}
        confirmButtonText={
          cardName === 'Playing'
            ? 'Opt out'
            : !isOptInButtonDisabled
              ? 'Opt in'
              : canMoveToWait
                ? 'Wait'
                : ''
        }
      />
      <ConfirmationModal
        open={showDoupleConfirmOptOutSinglePlayer}
        onClose={() => setShowDoupleConfirmOptOutSinglePlayer(false)}
        onConfirm={() => {
          setLoading(true);

          if (optOutSelection) {
            optOutMultiplePlayers(
              {
                leagueId: headerContent.details.LeagueID,
                slug,
                sessionId: headerContent.details.sessionId,
                matchDateId: headerContent.details.MatchDateID,
                players: [optOutSelection],
              },
              {
                onSuccess: (data) => {
                  if (data.message === 'Opted out successfully') {
                    toast.success(data.message);
                  } else {
                    toast.error(data.message);
                    setLoading(false);
                  }

                  queryClient.invalidateQueries(['league_rsvp']);
                },
                onError: () => {
                  toast.error('Something went wrong. Try again.');
                  setLoading(false);
                },
              }
            );
          }
          setShowDoupleConfirmOptOutSinglePlayer(false);
        }}
        title='Have to ask again. Are you sure?'
        message={`This will opt out ${selectedPlayer.fullName} ${
          selectedPlayerPartner && selectedPlayerPartner.fullName !== ''
            ? `and ${selectedPlayerPartner.fullName}`
            : ''
        } from this game
						day.`}
        confirmButtonText='Opt Out'
      />
      <ConfirmationModal
        open={showWaitlist}
        onClose={() => setShowWaitlist(false)}
        onConfirm={() => {
          setLoading(true);
          if (optOutSelection) {
            addOrRemoveFromWaitlist(
              {
                matchDateId: headerContent.details.MatchDateID,
                player: optOutSelection,
                doing: 'out',
              },
              {
                onSuccess: (data) => {
                  if (data.message) {
                    toast.success(data.message);
                  }
                  queryClient.invalidateQueries(['league_rsvp']);
                },
                onError: () => {
                  toast.error('Something went wrong. Try again.');
                  setLoading(false);
                },
              }
            );
          }
          setShowWaitlist(false);
        }}
        title='Are you sure?'
        message={`This will remove ${selectedPlayer.fullName} ${selectedPlayerPartner && selectedPlayerPartner.fullName !== '' ? `and ${selectedPlayerPartner.fullName}` : ''}`}
        confirmButtonText='Remove'
      />
      {showMessage && selectedPlayer ? (
        <SendMessageModal
          openModal={showMessage}
          setIsModalOpen={setShowMessage}
          title={`Send message to ${selectedPlayer.fullName}`}
          isAuthorized={isAuthorized}
          selectedPlayer={selectedPlayer}
          leagueId={headerContent.details.LeagueID}
          slug={slug}
          sessionId={headerContent.details.sessionId}
          matchDateId={headerContent.details.MatchDateID}
          leagueTitle={headerContent.details.leagueTitle}
          gameDayTitle={gameDayTitle}
        />
      ) : null}
    </>
  );
};
